import { pEventIterator } from 'p-event';

export interface AudioStreamWorkletProcessorMessage {
  pcmData: Uint8Array;
}

let sharedAudioContext: AudioContext | null = null;
let sharedMicrophoneStream: MediaStream | null = null;

async function getSharedAudioContext(): Promise<AudioContext> {
  if (sharedAudioContext && sharedAudioContext.state !== 'closed') {
    return sharedAudioContext;
  }

  sharedAudioContext = new AudioContext();

  return sharedAudioContext;
}

async function getMicrophoneStream(audioContext: AudioContext): Promise<MediaStream> {
  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }

  if (sharedMicrophoneStream) {
    return sharedMicrophoneStream;
  }

  sharedMicrophoneStream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });

  return sharedMicrophoneStream;
}

export function resetAudioState() {
  if (sharedMicrophoneStream) {
    sharedMicrophoneStream.getTracks().forEach((track) => track.stop());
    sharedMicrophoneStream = null;
  }

  if (sharedAudioContext && sharedAudioContext.state !== 'closed') {
    sharedAudioContext.close();
    sharedAudioContext = null;
  }
}

export async function createAudioStream(maxDurationInSeconds: number): Promise<{
  sampleRate: number;
  workletMessageIterator: AsyncIterableIterator<MessageEvent<AudioStreamWorkletProcessorMessage>>;
  destroy: () => void;
}> {
  const audioContext = await getSharedAudioContext();
  const microphoneStream = await getMicrophoneStream(audioContext);

  const audioSource = audioContext.createMediaStreamSource(microphoneStream);

  // Load the worklet module
  try {
    await audioContext.audioWorklet.addModule('/worklets/audio-stream-processor.js');
  } catch (error) {
    throw new Error(`AudioWorklet module failed to load: ${error instanceof Error ? error.message : String(error)}`);
  }

  // Transcribe service recommends a chunk duration of 50-200ms
  const sampleEmitFrequency = 1000 / 50;
  const bufferSize = Math.floor(audioContext.sampleRate / sampleEmitFrequency);

  const workletNode = new AudioWorkletNode(audioContext, 'AudioStreamWorkletProcessor', {
    processorOptions: { sampleBufferSize: bufferSize },
  });

  audioSource.connect(workletNode);

  const messageEventTarget = new EventTarget();
  const maxMessages = sampleEmitFrequency * maxDurationInSeconds;

  let isDestroyed = false;
  let messageCount = 0;

  workletNode.port.onmessage = (event) => {
    if (isDestroyed || messageCount >= maxMessages) {
      return;
    }
    messageCount++;
    const messageEvent = new MessageEvent('message', { data: event.data });
    messageEventTarget.dispatchEvent(messageEvent);
  };

  const workletMessageIterator = pEventIterator(messageEventTarget, 'message', {
    limit: maxMessages,
  }) as AsyncIterableIterator<MessageEvent<AudioStreamWorkletProcessorMessage>>;

  function destroy(): void {
    if (isDestroyed) return;
    isDestroyed = true;

    audioSource.disconnect(workletNode);
    workletNode.port.close();
  }

  return { sampleRate: audioContext.sampleRate, workletMessageIterator, destroy };
}
